import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  OnChanges,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
  WritableSignal,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SecurityStatus } from '@shared/enums/security-status';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { TaxResultApprovalService } from 'src/app/modules/tax-result/services/tax-result-approval.service';
import { SecurityDetails } from '../../models/security-details';
import { TaxResult } from '../../models/taxResult';
import { SecurityService } from '../../services/security.service';

@Component({
  standalone: false,
  selector: 'gstar-security-details',
  templateUrl: './security-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecurityDetailsComponent implements OnInit, OnChanges {
  private securityService = inject(SecurityService);
  private readonly router = inject(Router);
  private readonly taxResultApprovalService = inject(TaxResultApprovalService);
  private readonly translateService = inject(TranslateService);

  id: InputSignal<string> = input.required<string>();

  security: WritableSignal<SecurityDetails> = signal(new SecurityDetails());
  isApproved: Signal<boolean> = computed(
    () => this.security().statusId === SecurityStatus.Approved
  );
  isInformationRequired: Signal<boolean> = computed(
    () => this.security().statusId === SecurityStatus.InformationRequired
  );

  isSpecificAdviceTransactionType: Signal<boolean> = computed(
    () =>
      !!(
        this.security() &&
        this.security().transactionType &&
        this.security().transactionType!.transactionTypeId ===
          TransactionType.SpecificAdvice
      )
  );
  backButtonText!: string;
  routeBack!: string;

  groupTaxRatesByType: Signal<[string, TaxResult[]][]> = computed(() => {
    return Object.entries(
      this.securityService.groupBy(
        this.security().taxResults ?? [],
        i => i.taxResultItemTypeId ?? 0
      )
    );
  });

  ngOnInit(): void {
    this.backButtonText =
      !this.taxResultApprovalService.isEditingSecuritiesRules()
        ? this.translateService.instant('portfolio.backToPortfolio')
        : this.translateService.instant('portfolio.backToTaxResults');
    this.routeBack = !this.taxResultApprovalService.isEditingSecuritiesRules()
      ? '/portfolio'
      : 'tax-results/edit';
  }

  loadSecurity(): void {
    this.securityService
      .getSecurityById(parseInt(this.id()))
      .subscribe((securityDetails: SecurityDetails) => {
        securityDetails.taxResults = securityDetails.taxResults?.sort(a =>
          a.transactionTypeName === 'Capital Gains' ? 1 : -1
        );
        this.security.set(securityDetails);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id']) {
      const current = changes['id'].currentValue;
      const previous = changes['id'].previousValue;
      if (current !== previous) {
        this.loadSecurity();
      }
    }
  }

  redirectToPortfolio(): void {
    this.router.navigate([this.routeBack]);
  }

  multipleRatesString(item: TaxResult[]): string {
    if (item.length > 0) {
      return item.map((value: TaxResult) => value.rate).join('%, ');
    }
    return '';
  }
}
